import { config } from "./config";
import type { PlanId } from "./funnelData";

type FlowVariant = "a" | "b";

type CreateCheckoutSessionRequest = {
  email?: string;
  leadId?: number;
  planId: PlanId;
  flow: FlowVariant;
  currency?: string;
  offer?: string;
  value?: number;
  eventId?: string;
  fbp?: string;
  fbc?: string;
  externalId?: string;
  pageUrl?: string;
};

type CreateCheckoutSessionResponse = {
  checkoutUrl: string;
  sessionId: string;
  completionToken: string;
};

export type StripePlanPrice = {
  priceId: string;
  productId: string;
  productName: string | null;
  unitAmount: number;
  currency: string;
};

export type StripePricesResponse = Partial<Record<PlanId, StripePlanPrice>>;

export const createCheckoutSession = async (
  request: CreateCheckoutSessionRequest,
): Promise<CreateCheckoutSessionResponse> => {
  if (!config.apiBaseUrl) throw new Error("VITE_API_BASE_URL is not configured");

  const response = await fetch(`${config.apiBaseUrl}/api/checkout/create-session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    let detail = "";
    try {
      const body = (await response.json()) as { error?: string };
      detail = body.error ? `: ${body.error}` : "";
    } catch {
      //
    }
    throw new Error(`Failed to create checkout session: ${response.status}${detail}`);
  }

  return response.json() as Promise<CreateCheckoutSessionResponse>;
};

export const fetchPrices = async (offer?: string): Promise<StripePricesResponse> => {
  if (!config.apiBaseUrl) throw new Error("VITE_API_BASE_URL is not configured");
  const params = new URLSearchParams();
  if (offer) params.set("offer", offer);
  const query = params.toString();
  const response = await fetch(`${config.apiBaseUrl}/api/checkout/prices${query ? `?${query}` : ""}`);
  if (!response.ok) throw new Error(`Failed to fetch prices: ${response.status}`);
  return response.json() as Promise<StripePricesResponse>;
};

export const trackViewContentServerEvent = async (payload: {
  eventId: string;
  contentIds: PlanId[];
  value: number;
  currency: string;
  contentName?: string;
  fbp?: string | null;
  fbc?: string | null;
  externalId?: string;
  pageUrl?: string;
}): Promise<void> => {
  if (!config.apiBaseUrl) return;
  const response = await fetch(`${config.apiBaseUrl}/api/meta/view-content`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    keepalive: true,
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`Failed to track ViewContent: ${response.status}`);
};

export const trackLeadServerEvent = async (payload: {
  email: string;
  eventId: string;
  fbp?: string | null;
  fbc?: string | null;
  externalId?: string;
  pageUrl?: string;
}): Promise<void> => {
  if (!config.apiBaseUrl) return;
  const response = await fetch(`${config.apiBaseUrl}/api/leads/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    keepalive: true,
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`Failed to track lead: ${response.status}`);
};

export const trackPageViewServerEvent = async (payload: {
  eventId: string;
  fbp?: string | null;
  fbc?: string | null;
  externalId?: string;
  pageUrl?: string;
}): Promise<void> => {
  if (!config.apiBaseUrl) return;
  const response = await fetch(`${config.apiBaseUrl}/api/meta/page-view`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    keepalive: true,
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`Failed to track PageView: ${response.status}`);
};

export const captureLead = async (payload: {
  email: string;
  onboardingResponses: unknown;
  source: string;
}): Promise<{ leadId: number | null }> => {
  if (!config.apiBaseUrl) throw new Error("VITE_API_BASE_URL is not configured");
  const response = await fetch(`${config.apiBaseUrl}/api/leads/capture`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`Failed to save lead: ${response.status}`);
  return response.json() as Promise<{ leadId: number | null }>;
};
